import React, { useState, useEffect } from 'react';
import { database, ref, get } from '../../firebase';
import {
  DollarSign, 
  Filter, 
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Search,
  Shield,
  TrendingUp,
  Calendar,
  Users,
  Battery,
  X,
  AlertCircle,
  Check,
  Clock,
  Wallet,
  Zap
} from 'lucide-react';

const AUSTRALIA_TIMEZONE = 'Asia/Dubai';

const IncomeReport = () => {
  const [incomeData, setIncomeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [incomeType, setIncomeType] = useState('all');
  const [expandedRows, setExpandedRows] = useState([]);
  const [stats, setStats] = useState({
    totalIncome: 0,
    todayIncome: 0,
    levelIncome: 0,
    roiIncome: 0,
    energyIncome: 0,
    blockedIncome: 0,
    totalTransactions: 0,
    uniqueUsers: 0
  });
  const [walletBalances, setWalletBalances] = useState({
    totalROI: 0,
    totalEnergy: 0,
    usersWithROI: 0,
    usersWithEnergy: 0
  });

  useEffect(() => {
    fetchIncomeData();
  }, []);

  const getDateKeyInTimezone = (date, timezone = AUSTRALIA_TIMEZONE) => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);
  };

  const fetchIncomeData = async () => {
    try {
      setLoading(true);
      
      // Get today in fixed GST timezone
      const now = new Date();
      const australiaNow = new Date(now.toLocaleString('en-US', { timeZone: AUSTRALIA_TIMEZONE }));
      const todayStr = getDateKeyInTimezone(now, AUSTRALIA_TIMEZONE);
      
      console.log('Fetching all income data for admin...');
      
      const allIncomes = [];
      let totalIncome = 0;
      let todayIncome = 0;
      let levelIncome = 0;
      let blockedIncome = 0;
      const uniqueUsers = new Set();
      
      // Track wallet balances
      let totalROIWallet = 0;
      let totalEnergyWallet = 0;
      let usersWithROI = 0;
      let usersWithEnergy = 0;

      // ========== Fetch ALL users to get wallet balances ==========
      try {
        const usersRef = ref(database, 'users');
        const usersSnapshot = await get(usersRef);
        
        if (usersSnapshot.exists()) {
          const usersData = usersSnapshot.val();
          
          // Iterate through all registration dates
          Object.entries(usersData).forEach(([regDate, dateUsers]) => {
            if (!dateUsers) return;
            
            // Iterate through all users in that date
            Object.entries(dateUsers).forEach(([userKey, userData]) => {
              if (!userData) return;
              
              const userId = userData.userId || userData.sponsorId || userKey;
              const userName = userData.name || userData.userName || 'Unknown';
              
              // Get wallet balances
              const roiBalance = parseFloat(userData.wallets?.roi) || 0;
              const energyBalance = parseFloat(userData.wallets?.energy) || 0;
              
              // Add to totals
              if (roiBalance > 0) {
                totalROIWallet += roiBalance;
                usersWithROI++;
                
                // Add ROI wallet as an income record (current balance)
                allIncomes.push({
                  id: `roi_wallet_${userId}`,
                  date: todayStr,
                  displayDate: australiaNow.toLocaleDateString('en-US', {
                    timeZone: AUSTRALIA_TIMEZONE,
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  }),
                  displayTime: australiaNow.toLocaleTimeString('en-US', {
                    timeZone: AUSTRALIA_TIMEZONE,
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  }),
                  userId: userId,
                  userName: userName,
                  type: 'roi_income',
                  subType: 'ROI Wallet',
                  amount: roiBalance,
                  status: 'credited',
                  isBlocked: false,
                  fromUserName: 'System',
                  packageName: 'Accumulated ROI',
                  description: 'Current ROI wallet balance',
                  timestamp: new Date().toISOString(),
                  isWalletBalance: true
                });
                
                totalIncome += roiBalance;
                uniqueUsers.add(userId);
              }
              
              if (energyBalance > 0) {
                totalEnergyWallet += energyBalance;
                usersWithEnergy++;
                
                // Add Energy wallet as an income record (current balance)
                allIncomes.push({
                  id: `energy_wallet_${userId}`,
                  date: todayStr,
                  displayDate: australiaNow.toLocaleDateString('en-US', {
                    timeZone: AUSTRALIA_TIMEZONE,
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  }),
                  displayTime: australiaNow.toLocaleTimeString('en-US', {
                    timeZone: AUSTRALIA_TIMEZONE,
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  }),
                  userId: userId,
                  userName: userName,
                  type: 'energy_income',
                  subType: 'Energy Wallet',
                  amount: energyBalance,
                  status: 'credited',
                  isBlocked: false,
                  fromUserName: 'Downlines',
                  packageName: 'Accumulated Energy',
                  description: 'Current energy wallet balance',
                  timestamp: new Date().toISOString(),
                  isWalletBalance: true
                });
                
                totalIncome += energyBalance;
                uniqueUsers.add(userId);
              }
            });
          });
        }
      } catch (error) {
        console.error('Error fetching user wallets:', error);
      }

      // ========== Fetch Level Incomes from levelIncomes node ==========
      try {
        const levelRef = ref(database, 'levelIncomes');
        const levelSnapshot = await get(levelRef);
        
        if (levelSnapshot.exists()) {
          const levelData = levelSnapshot.val();
          
          // Iterate through dates
          Object.entries(levelData).forEach(([dateKey, dateData]) => {
            if (!dateData) return;
            
            // Iterate through users in that date
            Object.entries(dateData).forEach(([userId, userIncomes]) => {
              if (!userIncomes) return;
              
              // Iterate through each income record for this user
              Object.entries(userIncomes).forEach(([incomeId, income]) => {
                if (!income) return;
                
                const amount = parseFloat(income.amount) || 0;
                const isBlocked = income.status === 'blocked' || income.isBlocked === true;
                const incomeDate = income.date || income.createdAt || dateKey;
                
                if (!isBlocked) {
                  uniqueUsers.add(income.userId || userId);
                }
                
                // Parse timestamp for display
                let displayDate = dateKey;
                let displayTime = '';
                try {
                  const dateObj = new Date(incomeDate);
                  displayDate = dateObj.toLocaleDateString('en-US', {
                    timeZone: AUSTRALIA_TIMEZONE,
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  });
                  displayTime = dateObj.toLocaleTimeString('en-US', {
                    timeZone: AUSTRALIA_TIMEZONE,
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: true
                  });
                } catch (e) {}
                
                const incomeItem = {
                  id: `level_${incomeId}`,
                  originalId: incomeId,
                  date: dateKey,
                  displayDate: displayDate,
                  displayTime: displayTime,
                  userId: income.userId || userId,
                  userName: income.userName || income.fromUserName || 'Unknown',
                  type: 'level_income',
                  subType: `Level ${income.level || 1}`,
                  level: income.level || 1,
                  amount: amount,
                  status: income.status || (isBlocked ? 'blocked' : 'credited'),
                  isBlocked: isBlocked,
                  fromUserId: income.fromUserId,
                  fromUserName: income.fromUserName,
                  packageName: income.fromPackageName || income.packageName,
                  description: income.description || `Level ${income.level || 1} Commission`,
                  timestamp: income.timestamp || income.date || income.createdAt,
                  percentage: income.percentage
                };
                
                allIncomes.push(incomeItem);
                
                if (!isBlocked) {
                  totalIncome += amount;
                  levelIncome += amount;
                  
                  // Check if from today
                  const itemDateStr = getDateKeyInTimezone(new Date(incomeDate), AUSTRALIA_TIMEZONE);
                  if (itemDateStr === todayStr) {
                    todayIncome += amount;
                  }
                } else {
                  blockedIncome += amount;
                }
              });
            });
          });
        }
      } catch (error) {
        console.error('Error fetching level incomes:', error);
      }

      // Sort by date descending (newest first), but put wallet balances at bottom
      allIncomes.sort((a, b) => {
        if (a.isWalletBalance && !b.isWalletBalance) return 1;
        if (!a.isWalletBalance && b.isWalletBalance) return -1;
        
        const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
        const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
        return dateB - dateA;
      });

      console.log(`Found ${allIncomes.length} total income records`);
      console.log('Wallet Balances:', { totalROIWallet, totalEnergyWallet, usersWithROI, usersWithEnergy });

      setIncomeData(allIncomes);
      setWalletBalances({
        totalROI: totalROIWallet,
        totalEnergy: totalEnergyWallet,
        usersWithROI,
        usersWithEnergy
      });
      setStats({
        totalIncome,
        todayIncome,
        levelIncome,
        roiIncome: totalROIWallet,
        energyIncome: totalEnergyWallet,
        blockedIncome,
        totalTransactions: allIncomes.length,
        uniqueUsers: uniqueUsers.size
      });
    } catch (error) {
      console.error('Error fetching income data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const getTypeBadge = (type, subType, level) => {
    switch(type) {
      case 'level_income':
        const levelColors = {
          1: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
          2: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
          3: 'bg-green-500/20 text-green-400 border-green-500/30',
          4: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
          5: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
        };
        return {
          label: `Level ${level || 1}`,
          color: levelColors[level] || levelColors[1],
          icon: <Users className="h-3 w-3 mr-1" />
        };
      case 'roi_income':
        return {
          label: subType === 'ROI Wallet' ? 'ROI Wallet' : 'Daily ROI',
          color: 'bg-green-500/20 text-green-400 border-green-500/30',
          icon: <TrendingUp className="h-3 w-3 mr-1" />
        };
      case 'energy_income':
        return {
          label: 'Energy Wallet',
          color: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
          icon: <Battery className="h-3 w-3 mr-1" />
        };
      default:
        return {
          label: type,
          color: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
          icon: <DollarSign className="h-3 w-3 mr-1" />
        };
    }
  };

  const getStatusBadge = (status, isBlocked, isWalletBalance) => {
    if (isWalletBalance) {
      return {
        label: 'Current Balance',
        color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        icon: <Wallet className="h-3 w-3 mr-1" />
      };
    }
    if (isBlocked || status === 'blocked') {
      return {
        label: 'Blocked',
        color: 'bg-red-500/20 text-red-400 border-red-500/30',
        icon: <Shield className="h-3 w-3 mr-1" />
      };
    }
    return {
      label: 'Credited',
      color: 'bg-green-500/20 text-green-400 border-green-500/30',
      icon: <Check className="h-3 w-3 mr-1" />
    };
  };

  const filterData = () => {
    let filtered = incomeData;
    
    // Date filter - for wallet balances, always show
    if (dateFilter !== 'all') {
      const now = new Date();
      const australiaNow = new Date(now.toLocaleString('en-US', { timeZone: AUSTRALIA_TIMEZONE }));
      
      filtered = filtered.filter(item => {
        // Always include wallet balances
        if (item.isWalletBalance) return true;
        
        const itemDate = item.timestamp ? new Date(item.timestamp) : new Date(item.date);
        const australiaItemDate = new Date(itemDate.toLocaleString('en-US', { timeZone: AUSTRALIA_TIMEZONE }));
        
        switch(dateFilter) {
          case 'today':
            return australiaItemDate.toDateString() === australiaNow.toDateString();
          case 'week':
            const weekAgo = new Date(australiaNow);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return australiaItemDate >= weekAgo;
          case 'month':
            const monthAgo = new Date(australiaNow);
            monthAgo.setDate(monthAgo.getDate() - 30);
            return australiaItemDate >= monthAgo;
          default:
            return true;
        }
      });
    }
    
    // Type filter
    if (incomeType !== 'all') {
      filtered = filtered.filter(item => item.type === incomeType);
    }
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.userName?.toLowerCase().includes(term) ||
        item.userId?.toLowerCase().includes(term) ||
        item.fromUserName?.toLowerCase().includes(term) ||
        item.packageName?.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  };

  const toggleRowExpansion = (id) => {
    setExpandedRows(prev =>
      prev.includes(id)
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const filteredData = filterData();

  // Calculate summary stats for filtered data
  const filteredStats = {
    total: filteredData.reduce((sum, item) => sum + (item.isBlocked ? 0 : item.amount), 0),
    level: filteredData.filter(i => i.type === 'level_income' && !i.isBlocked).reduce((sum, i) => sum + i.amount, 0),
    roi: filteredData.filter(i => i.type === 'roi_income' && !i.isBlocked).reduce((sum, i) => sum + i.amount, 0),
    energy: filteredData.filter(i => i.type === 'energy_income' && !i.isBlocked).reduce((sum, i) => sum + i.amount, 0),
    count: filteredData.length
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 pb-8">
      <h1 className="text-2xl font-bold text-white mb-2">Income Report - All Types</h1>
      <p className="text-gray-400 mb-6">Complete income history including level commissions and current wallet balances</p>

      {/* Stats Cards - All Income Types */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">Total Income</div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.totalIncome)}</div>
          <div className="text-xs text-gray-500 mt-1">{stats.totalTransactions} records</div>
        </div>
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div className="text-gray-400 text-xs mb-1">Today</div>
          <div className="text-lg font-bold text-green-400">{formatCurrency(stats.todayIncome)}</div>
        </div>
        <div className="bg-yellow-500/10 rounded-xl p-4 border border-yellow-500/30">
          <div className="flex items-center gap-1 mb-1">
            <Users className="h-3 w-3 text-yellow-400" />
            <span className="text-yellow-400 text-xs">Level Income</span>
          </div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.levelIncome)}</div>
        </div>
        <div className="bg-green-500/10 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center gap-1 mb-1">
            <TrendingUp className="h-3 w-3 text-green-400" />
            <span className="text-green-400 text-xs">ROI Wallet</span>
          </div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.roiIncome)}</div>
          <div className="text-xs text-gray-400">{walletBalances.usersWithROI} users</div>
        </div>
        <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center gap-1 mb-1">
            <Battery className="h-3 w-3 text-purple-400" />
            <span className="text-purple-400 text-xs">Energy Wallet</span>
          </div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.energyIncome)}</div>
          <div className="text-xs text-gray-400">{walletBalances.usersWithEnergy} users</div>
        </div>
        <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
          <div className="flex items-center gap-1 mb-1">
            <Shield className="h-3 w-3 text-red-400" />
            <span className="text-red-400 text-xs">Blocked</span>
          </div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.blockedIncome)}</div>
        </div>
        <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
          <div className="text-gray-400 text-xs mb-1">Unique Users</div>
          <div className="text-lg font-bold text-blue-400">{stats.uniqueUsers}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gray-800/30 rounded-xl p-4 mb-6 border border-gray-700">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by user name, ID, from user, package..."
                className="w-full pl-10 pr-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm"
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
            
            <select
              value={incomeType}
              onChange={(e) => setIncomeType(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white text-sm"
            >
              <option value="all">All Types</option>
              <option value="level_income">Level Income</option>
              <option value="roi_income">ROI Income</option>
              <option value="energy_income">Energy Income</option>
            </select>
            
            <button
              onClick={fetchIncomeData}
              className="px-4 py-3 bg-primary hover:bg-primary/80 rounded-lg text-white flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block bg-gray-800/30 rounded-xl border border-gray-700 overflow-hidden mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr className="text-sm">
                <th className="py-4 px-4 text-left text-gray-400 font-medium">Date</th>
                <th className="py-4 px-4 text-left text-gray-400 font-medium">User</th>
                <th className="py-4 px-4 text-left text-gray-400 font-medium">Type</th>
                <th className="py-4 px-4 text-left text-gray-400 font-medium">From</th>
                <th className="py-4 px-4 text-left text-gray-400 font-medium">Package</th>
                <th className="py-4 px-4 text-left text-gray-400 font-medium">Amount</th>
                <th className="py-4 px-4 text-left text-gray-400 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30">
              {filteredData.map((item) => {
                const typeBadge = getTypeBadge(item.type, item.subType, item.level);
                const statusBadge = getStatusBadge(item.status, item.isBlocked, item.isWalletBalance);
                
                return (
                  <tr key={item.id} className={`hover:bg-gray-700/20 transition-colors ${item.isWalletBalance ? 'bg-blue-500/5' : ''}`}>
                    <td className="py-4 px-4 text-sm text-gray-300 whitespace-nowrap">
                      {item.displayDate}<br />
                      <span className="text-xs text-gray-500">{item.displayTime}</span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-white font-medium">{item.userName}</div>
                      <div className="text-xs text-gray-400 font-mono">{item.userId}</div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border ${typeBadge.color}`}>
                        {typeBadge.icon}
                        {typeBadge.label}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-white text-sm">{item.fromUserName || 'System'}</div>
                      {item.fromUserId && (
                        <div className="text-xs text-gray-400 font-mono">{item.fromUserId}</div>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <div className="text-white text-sm">{item.packageName || '-'}</div>
                      {item.percentage && (
                        <div className="text-xs text-gray-400">{item.percentage}%</div>
                      )}
                    </td>
                    <td className="py-4 px-4">
                      <span className={`font-bold ${item.isBlocked ? 'text-red-400' : 'text-green-400'}`}>
                        {formatCurrency(item.amount)}
                      </span>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs border ${statusBadge.color}`}>
                        {statusBadge.icon}
                        {statusBadge.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile View */}
      <div className="lg:hidden space-y-4">
        {filteredData.map((item) => {
          const typeBadge = getTypeBadge(item.type, item.subType, item.level);
          const statusBadge = getStatusBadge(item.status, item.isBlocked, item.isWalletBalance);
          const isExpanded = expandedRows.includes(item.id);
          
          return (
            <div key={item.id} className={`bg-gray-800/30 rounded-xl border ${item.isWalletBalance ? 'border-blue-500/30' : 'border-gray-700'} overflow-hidden`}>
              <button
                onClick={() => toggleRowExpansion(item.id)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-700/50 transition-colors"
              >
                <div className="text-left flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium text-white">{item.userName}</div>
                      <div className="text-xs text-gray-400 font-mono">{item.userId}</div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${item.isBlocked ? 'text-red-400' : 'text-green-400'}`}>
                        {formatCurrency(item.amount)}
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border mt-1 ${typeBadge.color}`}>
                        {typeBadge.icon}
                        {typeBadge.label}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <span>{item.displayDate}</span>
                    <span>•</span>
                    <span>{item.displayTime}</span>
                  </div>
                </div>
                
                <div className="ml-2">
                  {isExpanded ? (
                    <ChevronUp className="h-5 w-5 text-primary" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-primary" />
                  )}
                </div>
              </button>
             
              {isExpanded && (
                <div className="p-4 border-t border-gray-700 bg-gray-900/30">
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-gray-400 text-xs mb-1">From</div>
                        <div className="text-white text-sm">{item.fromUserName || 'System'}</div>
                        {item.fromUserId && (
                          <div className="text-xs text-gray-400 font-mono">{item.fromUserId}</div>
                        )}
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs mb-1">Status</div>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs border ${statusBadge.color}`}>
                          {statusBadge.icon}
                          {statusBadge.label}
                        </span>
                      </div>
                    </div>
                    
                    {item.packageName && (
                      <div>
                        <div className="text-gray-400 text-xs mb-1">Package</div>
                        <div className="text-white text-sm">{item.packageName}</div>
                        {item.percentage && (
                          <div className="text-xs text-gray-400">{item.percentage}% commission</div>
                        )}
                      </div>
                    )}
                    
                    <div>
                      <div className="text-gray-400 text-xs mb-1">Description</div>
                      <div className="text-white text-sm">{item.description}</div>
                    </div>
                    
                    {item.isWalletBalance && (
                      <div className="mt-2 p-2 bg-blue-500/10 rounded-lg border border-blue-500/30">
                        <p className="text-xs text-blue-400">
                          ℹ️ Current wallet balance (not a transaction)
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        {filteredData.length === 0 && (
          <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700">
            <div className="text-5xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-white mb-2">No Income Records</h3>
            <p className="text-gray-400">Try adjusting your search filters</p>
          </div>
        )}
      </div>

      {/* Summary with Type Breakdown */}
      {filteredData.length > 0 && (
        <div className="mt-6 p-4 bg-gray-800/30 rounded-xl border border-gray-700">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <span className="text-gray-400 text-sm">Showing:</span>
              <span className="text-white font-bold ml-2">{filteredData.length} records</span>
            </div>
            <div>
              <span className="text-gray-400 text-sm">Total:</span>
              <span className="text-green-400 font-bold ml-2">{formatCurrency(filteredStats.total)}</span>
            </div>
            <div>
              <span className="text-gray-400 text-sm">Level:</span>
              <span className="text-yellow-400 font-bold ml-2">{formatCurrency(filteredStats.level)}</span>
            </div>
            <div>
              <span className="text-gray-400 text-sm">ROI:</span>
              <span className="text-green-400 font-bold ml-2">{formatCurrency(filteredStats.roi)}</span>
            </div>
            <div>
              <span className="text-gray-400 text-sm">Energy:</span>
              <span className="text-purple-400 font-bold ml-2">{formatCurrency(filteredStats.energy)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomeReport;
